import { BillAiFlowStep } from '../state/bill-ai-flow.models';

/**
 * A human-friendly, actionable explanation of a backend import error.
 * Produced by translateImportError() from raw error text returned by api-core.
 */
export interface FriendlyImportError {
  title: string;
  message: string;
  action?: { label: string; targetStep: BillAiFlowStep };
  rawError: string;
}

/**
 * Translate a raw backend error message into a friendly, actionable error.
 *
 * Pattern-matches on error strings emitted by api-core's bill-import endpoint.
 * Branch ordering is important — most-specific patterns (e.g. "totalSupplyRate")
 * must come before more-generic ones (e.g. plain "rate" or "total") to avoid
 * misrouting users to the wrong step.
 *
 * NOTE: These patterns match error strings produced by api-core. If api-core
 * rewords any of these, update the branches below AND add a regression test
 * in translate-import-error.spec.ts.
 *
 *   - "totalSupplyRate must be between..."     → bill rate validator in
 *                                                 SharedBulkImporterValidations
 *   - "totalDistributionRate must be between..." → same file
 *   - "Unit is required."                      → GasRequiredFieldsRule
 *                                                 (bill importer, gas bills)
 *   - "could not resolve legal entity"         → AbstractBulkImporterDAO
 *                                                 (resolveLenForDto)
 *   - "legal entity ... not found"             → BillAiImportService
 *                                                 (validateEntityOwnership)
 *   - "Two legal entities are linked to..."    → frontend buildImportRequest
 *                                                 sanity check
 *   - "ZIP" / "zip"                            → frontend summary validation
 *                                                 + backend address validators
 */
export function translateImportError(rawError: string): FriendlyImportError {
  const err = rawError || '';
  const lower = err.toLowerCase();

  // --- Most-specific patterns first ---

  // Supply rate out of range (before generic "rate" or "supply" checks)
  if (lower.includes('totalsupplyrate') || lower.includes('supply rate')) {
    return {
      title: 'A supply rate on one of your bills is out of range',
      message: "The supply charges on one or more bills produce a rate the system considers invalid (under $0.01 or over $0.80 per unit). Go back to the Bills step, open the affected bill, and double-check the Supply Charges and Usage Amount.",
      action: { label: 'Go to Bills', targetStep: 'bill-review' },
      rawError: err,
    };
  }

  // Distribution rate out of range
  if (lower.includes('totaldistributionrate') || lower.includes('distribution rate')) {
    return {
      title: 'A distribution rate on one of your bills is out of range',
      message: "The distribution charges on one or more bills produce a rate the system considers invalid. Go back to the Bills step and verify the Distribution Charges and Usage Amount.",
      action: { label: 'Go to Bills', targetStep: 'bill-review' },
      rawError: err,
    };
  }

  // Gas bill unit — GasRequiredFieldsRule emits "Unit is required." for a gas bill
  // with no unit. After the rate branches: their messages can name a unit too.
  if (/\bunits?\b/.test(lower) && (lower.includes('required') || lower.includes('incorrect unit'))) {
    return {
      title: "A gas bill's usage unit was not accepted",
      message: "Every natural gas bill needs a usage unit (Therms, Dth, Ccf…). Go back to the Bills step, open each gas bill, and make sure the Unit matches the one printed on the bill.",
      action: { label: 'Go to Bills', targetStep: 'bill-review' },
      rawError: err,
    };
  }

  // Two LENs linked to the same record (frontend sanity check)
  if (lower.includes('two legal entities are linked') || lower.includes('same existing record')) {
    return {
      title: 'Two legal entities are linked to the same record',
      message: "You have two different legal entities both matched to the same existing record in the system. Go back to the Legal Entities step and change one of the matches.",
      action: { label: 'Go to Legal Entities', targetStep: 'len' },
      rawError: err,
    };
  }

  // Energy plan issues (before generic "meter" check — a message like
  // "Meter 123 has no energy plan" should route to Bills, not Meters)
  if (lower.includes('energy plan') || lower.includes('energyplan')) {
    return {
      title: 'Energy plan could not be resolved',
      message: "A supplier bill is missing an energy plan selection, or the plan couldn't be matched. Go back to the Bills step to select or create a plan.",
      action: { label: 'Go to Bills', targetStep: 'bill-review' },
      rawError: err,
    };
  }

  // Totals don't add up
  if (lower.includes('total') && (lower.includes('sum') || lower.includes('add up') || lower.includes('mismatch'))) {
    return {
      title: "Bill totals don't add up",
      message: "The Total Amount on a bill doesn't match the sum of Supply + Distribution charges. Go back to Bills and fix the affected bill — you can use the calculator button next to the total to auto-sync.",
      action: { label: 'Go to Bills', targetStep: 'bill-review' },
      rawError: err,
    };
  }

  // Existing meter with the same account number — BulkImportService emits
  // "Meter UAN '<uan>' already exists in the system". Must run before the
  // generic meter branch, which would mislabel this as missing information.
  if (lower.includes('meter uan') && lower.includes('already exists')) {
    return {
      title: 'A meter with this account number already exists',
      message: "A meter with the same utility account number already exists at this service address. Go back to the Meters step and link to the existing meter instead of creating a new one.",
      action: { label: 'Go to Meters', targetStep: 'meter' },
      rawError: err,
    };
  }

  // Duplicate or overlapping bill — but NOT "legal entities linked to same record"
  // (handled above), and NOT generic "overlap" in other contexts.
  if (lower.includes('duplicate bill') || lower.includes('overlapping bill') || lower.includes('bill ... overlap') || (lower.includes('bill') && lower.includes('overlap'))) {
    return {
      title: 'A bill overlaps with an existing bill',
      message: "One of your bills has dates that overlap with a bill already in the system. Go back to the Meters step to resolve the duplicate.",
      action: { label: 'Go to Meters', targetStep: 'meter' },
      rawError: err,
    };
  }

  // LEN resolution (could not resolve, not found, does not belong)
  if (lower.includes('legal entity') || lower.includes('customerlenv4entity')) {
    return {
      title: 'A legal entity could not be resolved',
      message: "One of the legal entities you matched couldn't be found or has inconsistent data. Go back to the Legal Entities step and re-select the match.",
      action: { label: 'Go to Legal Entities', targetStep: 'len' },
      rawError: err,
    };
  }

  // Meter issues — require meter AND a meter-specific qualifier to avoid
  // matching addresses or bills that happen to mention "meter".
  if (lower.includes('meter') && (lower.includes('utility') || lower.includes('commodity') || lower.includes('account number') || lower.includes('uan'))) {
    return {
      title: 'A meter is missing required information',
      message: "One of your meters is missing a utility, commodity type, or account number. Go back to the Meters step to fix it.",
      action: { label: 'Go to Meters', targetStep: 'meter' },
      rawError: err,
    };
  }

  // Service address — ZIP is most specific
  if ((lower.includes('service address') || lower.includes('address')) && (lower.includes('zip') || lower.includes('postal'))) {
    return {
      title: 'A service address is missing a ZIP code',
      message: "One of your service addresses doesn't have a ZIP code. Go back to the Addresses step and fill in the missing ZIP.",
      action: { label: 'Go to Addresses', targetStep: 'sa' },
      rawError: err,
    };
  }
  if (lower.includes('service address') || lower.includes('address')) {
    return {
      title: 'A service address needs attention',
      message: "One of your service addresses has missing or invalid data. Go back to the Addresses step to fix it.",
      action: { label: 'Go to Addresses', targetStep: 'sa' },
      rawError: err,
    };
  }

  // Network / timeout
  if (lower.includes('network') || lower.includes('timeout') || lower.includes('connection')) {
    return {
      title: 'Connection issue',
      message: "We couldn't reach the server. Check your connection and try the import again.",
      rawError: err,
    };
  }

  // Fallback — unknown backend error
  return {
    title: 'Import failed',
    message: "The server rejected the import. Review the details below and try again, or go back through the steps to verify your data.",
    rawError: err,
  };
}

/**
 * Translate a list of raw error messages into a de-duplicated list of
 * friendly errors (deduped by title so users see one card per issue kind).
 */
export function translateImportErrors(rawErrors: string[]): FriendlyImportError[] {
  const seen = new Set<string>();
  const result: FriendlyImportError[] = [];
  for (const msg of rawErrors) {
    const friendly = translateImportError(msg);
    if (!seen.has(friendly.title)) {
      seen.add(friendly.title);
      result.push(friendly);
    }
  }
  return result;
}
